import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Briefcase, 
  CheckCircle, 
  Mail, 
  Lock, 
  User as UserIcon,
  Loader2,
  Eye,
  EyeOff,
  Sparkles,
  FileText,
  Save
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("signup"); // Default to signup for conversion
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Check if user came with generated prep to save
  const hasPendingPrep = location.state?.hasPendingPrep || sessionStorage.getItem('hireun_generated_prep');
  const prepToSave = location.state?.prepToSave || sessionStorage.getItem('hireun_generated_prep');
  const customMessage = location.state?.message;
  
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  
  // Signup form
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirm, setSignupConfirm] = useState("");
  
  // Handle email verification from URL
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      verifyEmail(token);
    }
  }, [searchParams]);

  // Set default tab based on context
  useEffect(() => {
    if (hasPendingPrep) {
      setActiveTab("signup"); // Encourage signup for new users
    }
  }, [hasPendingPrep]);
  
  const verifyEmail = async (token) => {
    try {
      await axios.post(`${API}/auth/verify-email`, { token });
      toast.success("Email verified! You can now log in.");
      setActiveTab("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      // Get the Google OAuth URL from our backend
      const response = await axios.get(`${API}/auth/google/login`, {
        withCredentials: true
      });
      
      if (response.data.auth_url) {
        // Redirect to Google OAuth
        window.location.href = response.data.auth_url;
      } else {
        toast.error("Failed to initiate Google sign-in");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Google login error:", error);
      toast.error(error.response?.data?.detail || "Failed to start Google sign-in");
      setIsLoading(false);
    }
  };
  
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    // If 2FA is required and no code provided
    if (requires2FA && !totpCode) {
      toast.error("Please enter your 2FA code");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword,
        totp_code: totpCode || undefined
      }, { withCredentials: true });
      
      // Check if 2FA is required (202 status)
      if (response.status === 202 && response.data.requires_2fa) {
        setRequires2FA(true);
        toast.info("Please enter your 2FA code");
        setIsLoading(false);
        return;
      }
      
      // Store user data and session token for session persistence
      if (response.data.user_id) {
        localStorage.setItem('hireun_user', JSON.stringify(response.data));
        // Store session token separately for Authorization header
        if (response.data.session_token) {
          localStorage.setItem('hireun_session_token', response.data.session_token);
        }
      }
      
      toast.success("Logged in successfully!");
      // Navigate with user data in state
      navigate("/app", { state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!signupName || !signupEmail || !signupPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (signupPassword !== signupConfirm) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (signupPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        name: signupName,
        email: signupEmail,
        password: signupPassword
      });
      
      if (response.data.email_verified) {
        toast.success("Account created! You can now log in.");
        setActiveTab("login");
        setLoginEmail(signupEmail);
      } else {
        toast.success("Account created! Please check your email to verify.");
      }
      
      // Clear form
      setSignupName("");
      setSignupEmail("");
      setSignupPassword("");
      setSignupConfirm("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signup failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-accent/50 to-background">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-primary" />
              <span className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Hireun
              </span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex items-center justify-center min-h-[calc(100vh-64px)] px-4 py-8">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="p-8">
            {/* Show save prep banner if user has generated prep */}
            {prepToSave && (
              <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                    <Save className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-foreground">Save Your Interview Prep</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {customMessage || "Create an account to save this interview prep and access it anytime."}
                    </p>
                  </div>
                </div>
                {/* Skip option */}
                <div className="mt-3 pt-3 border-t border-primary/10">
                  <button 
                    onClick={() => {
                      sessionStorage.removeItem('hireun_generated_prep');
                      navigate('/');
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Skip for now → Continue without saving
                  </button>
                </div>
              </div>
            )}

            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-7 h-7 text-primary" />
              </div>
              <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {hasPendingPrep ? "Almost There!" : "Welcome to Hireun"}
              </h1>
              <p className="text-muted-foreground text-sm">
                {hasPendingPrep ? "Sign up to access your interview prep" : "AI-powered interview preparation"}
              </p>
            </div>

            {/* Google Login */}
            <Button 
              onClick={handleGoogleLogin}
              className="w-full h-11 bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 shadow-sm mb-2"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
            <p className="text-xs text-center text-muted-foreground mb-6">
              You'll be redirected to Google's secure login
            </p>
            
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-muted-foreground">or continue with email</span>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="login">Log In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
              
              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        type="email"
                        placeholder="Email address"
                        className="pl-10"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        data-testid="login-email"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        className="pl-10 pr-10"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* 2FA Code Input */}
                  {requires2FA && (
                    <div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input 
                          type="text"
                          placeholder="Enter 2FA code"
                          className="pl-10 text-center tracking-widest font-mono"
                          maxLength={6}
                          value={totpCode}
                          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          data-testid="login-2fa-code"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Enter the 6-digit code from your authenticator app</p>
                    </div>
                  )}
                  
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading}
                    data-testid="login-submit"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {requires2FA ? "Verify & Log In" : "Log In"}
                  </Button>
                </form>
              </TabsContent>
              
              {/* Signup Tab */}
              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-4">
                  <div>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        type="text"
                        placeholder="Full name"
                        className="pl-10"
                        value={signupName}
                        onChange={(e) => setSignupName(e.target.value)}
                        data-testid="signup-name"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        type="email"
                        placeholder="Email address"
                        className="pl-10"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        data-testid="signup-email"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        type={showPassword ? "text" : "password"}
                        placeholder="Password (min 8 characters)"
                        className="pl-10 pr-10"
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        data-testid="signup-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm password"
                        className="pl-10"
                        value={signupConfirm}
                        onChange={(e) => setSignupConfirm(e.target.value)}
                        data-testid="signup-confirm"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading}
                    data-testid="signup-submit"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {hasPendingPrep ? "Create Account & View Prep" : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="mt-6 space-y-2">
              {[
                "Personalized interview scripts",
                "Save & access preparations anywhere",
                "AI-powered content refinement"
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {benefit}
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t text-center">
              <p className="text-sm text-muted-foreground">
                Just exploring?{" "}
                <Link to="/" className="text-primary hover:underline">
                  Try without an account
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LoginPage;
